import {DISPLAY_TYPES} from 'cl-form/FormSections/consts';
import {flatten} from 'flat';
import * as _ from 'lodash';
import {createSelector} from 'reselect';
import {StoreState} from '../../../App/initialStore';
import {MyAccount} from '../../../App/routeConstants';
import {deleteVariation, isDraftItemId} from '../../../shared/helpers/EbayListingsGeneralHelpers';
import {isEbayImage} from '../../ViewDesigns/helpers/resizeImageUrlHelper';
import {STATUS_FILTERS} from '../consts';
import {refreshSellerStoreCategories} from '../listingsHelper';
import {Map} from 'immutable';
import {
  ADD_OR_EDIT,
  DEFAULT_CURRENCIES,
  DEFAULT_FIELD_PROPS,
  DEFAULT_VALUES,
  DISABLED,
  DISABLED_BY_PRODUCT,
  DISABLED_BY_VARIATION,
  ENABLED,
  FIELD_NAMES,
  GTC,
  PRODUCT_IDENTIFIER_FIELDS,
  INPUT_TYPES,
  isItemSpecificsFieldName,
  ITEM_DESCRIPTION_SECTION,
  LISTING_TYPES,
  MEASUREMENT_UNITS,
  MEASUREMENT_UNITS_TYPES,
  PHOTOS_SECTION,
  POLICIES_SECTION,
  PRODUCT_SECTION,
  REQUIRED,
  SELLING_SECTION,
  SHOW_MODES,
  USE_DEFAULT_PHOTOS,
  VARIATIONS_SECTION,
  VARIATIONS_SELECTED_ASPECT,
  getItemSpecificWithoutPrefix,
  PRODUCT_LISTING_DETAILS_PREFIX,
} from './consts';
import {ColumnsType, ColumnType} from 'cl-table/redux/actions';
import {compose, nonEmpty, positive} from 'cl-form/validation/validators';
import {isNumericType} from 'cl-table/VirtualizedHelpers';
import {getCurrentState} from '../../../shared/ActionsManager/EbayListingsActions/currentState';

const shallowEqual = require('fbjs/lib/shallowEqual');
export type flattenNestedVariationsType = {
  recommendations: string[];
  parentName: string;
  parentValue: string;
  childName: string;
};

export function getEbayDetails(state: StoreState) {
  return _.get(state, 'ebayListing.categorySiteData.eBayDetails');
}

export const isNonDraftItemId = itemId => {
  return itemId && !isDraftItemId(itemId);
};
export const isNonDraft = state => {
  const {itemId} = state.routing.location.query;
  return isNonDraftItemId(itemId);
};

const getSellerPolicies = (state: StoreState): any => state.ebayListing.sellerPolicies;
export const getEbayListing = (state: StoreState) => state.ebayListing.listingData;
const getInternalFieldsToRevise = (state: StoreState) => state.ebayListing.fieldsToRevise;
const getFieldsToDelete = (state: StoreState) => state.ebayListing.fieldsToDelete;
const getEbayListings = (state: StoreState) => state.listingsPage.listings;
const getSellerShippingPolicies = state => _.get(state.ebayListing.sellerPolicies, 'SHIPPING');
const getCategoryFeatures = (state: StoreState) => _.get(state, 'ebayListing.categorySiteData.features');
export const getProductData = (state: StoreState) => _.get(state, 'ebayListing.productData');
export const getItemId = (state: StoreState) => state.routing.location.query.itemId;
export const getCategorySpecifics = (state: StoreState) => _.get(state, 'ebayListing.categorySiteData.specifics');
export const getSellerId = (state: StoreState) =>
  state.routing.location.query.selectedSellerId &&
  state.listingsPage &&
  state.listingsPage.listings[state.routing.location.query.selectedSellerId]
    ? state.routing.location.query.selectedSellerId
    : null;
export const addOrEdit = (state: StoreState) => (getItemId(state) ? ADD_OR_EDIT.EDIT : ADD_OR_EDIT.ADD);
export const getEbaySites = (state: StoreState) => state.general.eBaySites;
export const isBusinessPolicyOptedIn = (state: StoreState) => _.get(state, 'ebayListing.sellerPolicies.optedIn');
export const getSiteToName = createSelector(
  [getEbaySites],
  ebaySites => {
    return _.mapValues(_.keyBy(ebaySites, 'site'), 'text');
  }
);

export const getEbayListingFromListingsPage = createSelector(
  [getItemId, getEbayListings, getSellerId],
  (itemId, ebayListings, sellerId) => {
    const sellerListings = ebayListings && ebayListings[sellerId];
    return _.reduce(
      STATUS_FILTERS,
      (listing, statusFilter) => {
        return listing || (sellerListings && sellerListings[statusFilter].listings.get(itemId));
      },
      null
    );
  }
);

export const getVariationSpecificsSet = (state: StoreState) =>
  getUpdatedFieldValueInternal(state, FIELD_NAMES.VARIATIONS_SPECIFIC_SET);

export const getVariationAspectNames = createSelector(
  [getVariationSpecificsSet],
  variationSpecificsSet => {
    return _.keys(variationSpecificsSet);
  }
);

export const getVariationSpecifics = createSelector(
  [getCategorySpecifics],
  itemSpecifics => {
    return _.pickBy(itemSpecifics, value => {
      return _.isUndefined(value.rules.VariationSpecifics);
    }); // If VariationSpecifics rule is defined, it's always 'Disabled'
  }
);

export const getNonNestedVariationSpecifics = createSelector(
  [getVariationSpecifics],
  variationSpecifics => {
    return _.pickBy(variationSpecifics, value => {
      return _.isUndefined(value.children);
    });
  }
);

export const getNestedVariationSpecifics = createSelector(
  [getVariationSpecifics],
  variationSpecifics => {
    return _.pickBy(variationSpecifics, value => {
      return !_.isUndefined(value.children);
    });
  }
);

export const getVariationSpecificNames = createSelector(
  [getVariationSpecifics, getNestedVariationSpecifics],
  (variationSpecifics, nestedVariationSpecifics) => {
    let totalSpecifics = {...variationSpecifics};
    _.forEach(nestedVariationSpecifics, nestedSpecific => {
      totalSpecifics = {...totalSpecifics, ...nestedSpecific.children};
    });
    return _.reduce(totalSpecifics, (specifics, value, name) => [...specifics, getItemSpecificFieldName(name)], []);
  }
);

export const getFlatNestedVariationsSpecifics = createSelector(
  [getNestedVariationSpecifics],
  nestedVariationSpecifics => {
    const result = {};
    _.forEach(nestedVariationSpecifics, (specificData, specificName) => {
      _.forEach(specificData.children, (childData, childName) => {
        // There should be only one child
        _.forEach(childData.recommendations, (recommendations, parentValue) => {
          result[specificName + ' / ' + parentValue + ' / ' + childName] = {
            recommendations: childData.recommendations[parentValue],
            parentName: specificName,
            parentValue: parentValue,
            childName: childName,
          };
        });
      });
    });
    return result;
  }
);

export const getVariationSpecificsForEditDialog = createSelector(
  [getNonNestedVariationSpecifics, getFlatNestedVariationsSpecifics],
  (nonNestedVariationSpecifics, flattenNestedVariationsSpecifics) => {
    const result = {...nonNestedVariationSpecifics, ...flattenNestedVariationsSpecifics};
    const sortedResult = {};
    _.forEach(_.keys(result).sort(), key => {
      // Edit dialog should show specifics in alphabetical order
      sortedResult[key] = result[key];
    });
    return sortedResult;
  }
);

function getStoreCategoriesNames(categories) {
  const storeCategoryNames = [];
  _.forEach(categories, category => {
    if (category.ChildCategory) {
      storeCategoryNames.push(
        getStoreCategoriesNames(
          _.mapValues(category.ChildCategory, value => ({...value, Name: category.Name + ' > ' + value.Name}))
        )
      );
    } else {
      storeCategoryNames.push(category);
    }
  });
  return _.flattenDeep(storeCategoryNames);
}

const getPreLoadedSellerStoreCategories = (state, sellerId) => {
  const storeCategories =
    sellerId &&
    state.listingsPage.listings[sellerId].sellerStoreCategories &&
    state.listingsPage.listings[sellerId].sellerStoreCategories.CustomCategory;
  if (storeCategories) {
    return getStoreCategoriesNames(storeCategories);
  }
  return false;
};

export const getStoreCategoriesBySellerId = async (state, sellerId) => {
  const storeCategories = getPreLoadedSellerStoreCategories(state, sellerId);
  if (storeCategories) {
    return storeCategories;
  }
  const sellerStoreCategories = await refreshSellerStoreCategories(sellerId);
  return sellerStoreCategories && sellerStoreCategories.CustomCategory
    ? getStoreCategoriesNames(sellerStoreCategories.CustomCategory)
    : false;
};

export const getSellerStoreCategories = state => {
  return getPreLoadedSellerStoreCategories(state, getSellerId(state));
};

export function getItemSpecificFieldName(itemSpecificName) {
  return `${FIELD_NAMES.ITEM_SPECIFICS}.${itemSpecificName}`;
}

function getItemSpecificProps(
  listingData,
  variationAspects,
  {recommendations, rules: {MaxValues, Relationship, SelectionMode, MinValues}, children},
  itemSpecificName
) {
  const isChild = !!Relationship;
  let displayType;
  const value = _.get(listingData, getItemSpecificFieldName(itemSpecificName));
  let values;
  if (_.isArray(value)) {
    values = _.mapKeys(value, value => value);
  } else if (value) {
    values = {[value]: value};
  }
  const rawDataSet = isChild ? recommendations : _.mapKeys(recommendations);
  const dataSet = _.assign({}, values, _.omitBy(rawDataSet, recommendation => _.isEmpty(recommendation)));
  if (_.isEmpty(recommendations)) {
    displayType = DISPLAY_TYPES.FREE_TEXT;
  } else if (MaxValues === 1) {
    displayType = DISPLAY_TYPES.SINGLE_SELECT;
  } else {
    displayType = DISPLAY_TYPES.MULTI_SELECT;
  }
  return [
    {
      maxValues: MaxValues,
      freeText: SelectionMode === INPUT_TYPES.FREE_TEXT,
      displayType,
      dataSet,
      parentName: isChild ? getItemSpecificFieldName(Relationship[0].ParentName) : undefined,
      floatingLabelText: itemSpecificName,
      fieldName: getItemSpecificFieldName(itemSpecificName), // used for key by but must be passed by render
      required: MinValues >= 1 && _.indexOf(variationAspects, itemSpecificName) === -1,
    },
    ..._.flatMap(children, (childSpecifics, childName) =>
      getItemSpecificProps(listingData, variationAspects, childSpecifics, childName)
    ),
  ];
}

export const getFlattenItemSpecificsProps = (categorySpecifics, listingData, variationAspects) => {
  const itemSpecificsProps = _.flatMap(categorySpecifics, (itemSpecific, itemSpecificName) => {
    return getItemSpecificProps(listingData, variationAspects, itemSpecific, itemSpecificName);
  });
  return _.keyBy(itemSpecificsProps, 'fieldName');
};

export const getUpdatedFieldValueInternal = (state: StoreState, fieldName: string) =>
  getInternalFieldsToRevise(state)[fieldName] || _.get(getEbayListing(state), fieldName) || DEFAULT_VALUES[fieldName];
const getConditionValue = state => getUpdatedFieldValueInternal(state, FIELD_NAMES.CONDITION_ID);
export const getConditionEnableFromCategoryFeatures = features => _.get(features, 'conditionEnabled') !== DISABLED;
const getConditionEnable = state => getConditionEnableFromCategoryFeatures(getCategoryFeatures(state));
export const getVariationsEnable = state => _.get(getCategoryFeatures(state), 'VariationsEnabled') === true;
const getShippingType = state => {
  const sellerShippingPolicies = getSellerShippingPolicies(state);
  const currentShippingPolicyProfileId = getUpdatedFieldValueInternal(state, FIELD_NAMES.SHIPPING_POLICY);
  const currentShippingPolicy =
    sellerShippingPolicies &&
    _.find(sellerShippingPolicies, ({profileId}) => profileId === currentShippingPolicyProfileId);
  return _.get(currentShippingPolicy, 'shippingPolicyInfo.domesticShippingType');
};

const getListingTypeInternal = state => getUpdatedFieldValueInternal(state, FIELD_NAMES.LISTING_TYPE);
const getSiteInternal = state => getUpdatedFieldValueInternal(state, FIELD_NAMES.EBAY_SITE);
const getPictureURLInternal = state => getUpdatedFieldValueInternal(state, FIELD_NAMES.PICTURE_URL);
const getCategoryInternal = state => getUpdatedFieldValueInternal(state, FIELD_NAMES.PRIMARY_CATEGORY);

export const getEbayListingWithItemSpecificsRemovedIfNeeded = createSelector(
  [getEbayListing, getSiteInternal, getCategoryInternal],
  (listingData, site, category) => {
    if (
      _.get(listingData, FIELD_NAMES.EBAY_SITE) === site &&
      _.get(listingData, FIELD_NAMES.PRIMARY_CATEGORY) === category
    ) {
      return listingData;
    }
    return _.omit(listingData, FIELD_NAMES.ITEM_SPECIFICS);
  }
);

export const getItemSpecificsProps = createSelector(
  [getCategorySpecifics, getEbayListingWithItemSpecificsRemovedIfNeeded, getVariationAspectNames],
  (categorySpecifics, listingData, variationAspects) => {
    return getFlattenItemSpecificsProps(categorySpecifics, listingData, variationAspects);
  }
);

const getEbayGalleryUrls = createSelector(
  [getFieldsToDelete, getPictureURLInternal],
  (fieldsToDelete, pictureURLInternal) => {
    return fieldsToDelete[FIELD_NAMES.PICTURE_URL] ? [] : pictureURLInternal;
  }
);
const getDescriptionGalleryUrls = state => {
  return getFieldsToDelete(state)[FIELD_NAMES.CL_PICTURE_URLS]
    ? []
    : getUpdatedFieldValueInternal(state, FIELD_NAMES.CL_PICTURE_URLS);
};

const getDefaultTemplate = state => state.templates.userTemplates.default;
const getDefaultTemplateData = createSelector(
  [getDefaultTemplate],
  defaultTemplate => {
    return {
      [FIELD_NAMES.TEMPLATE_ID]: defaultTemplate.id,
      [FIELD_NAMES.TEMPLATE_NAME]: defaultTemplate.name,
      [FIELD_NAMES.TEMPLATE_PREVIEW_URL]: defaultTemplate.previewUrl,
    };
  }
);

const getTemplateId = state => {
  return (
    getUpdatedFieldValueInternal(state, FIELD_NAMES.TEMPLATE_ID) ||
    (addOrEdit(state) === ADD_OR_EDIT.ADD && getDefaultTemplateData(state)[FIELD_NAMES.TEMPLATE_ID])
  );
};

export const getMeasurementUnitTypeFromSite = site => {
  return _.includes(['US', 'eBayMotors', 'Netherlands'], site)
    ? MEASUREMENT_UNITS_TYPES.ENGLISH
    : MEASUREMENT_UNITS_TYPES.METRIC;
};

const getMeasurementUnitType = state => {
  const site = getSiteInternal(state);
  return getMeasurementUnitTypeFromSite(site);
};

const getCurrency = state => getUpdatedFieldValueInternal(state, FIELD_NAMES.CURRENCY);

export const getDefaultValues = createSelector(
  [getSiteInternal, getListingTypeInternal, getDefaultTemplateData, addOrEdit, getMeasurementUnitType, getEbayDetails],
  (site, listingType, defaultTemplateData, addOrEdit, measurementUnitType, eBayDetails) => {
    if (addOrEdit === ADD_OR_EDIT.EDIT) {
      return listingType === LISTING_TYPES.FIXED_PRICE ? {[FIELD_NAMES.DURATION]: GTC} : {};
    }
    return {
      ...DEFAULT_VALUES,
      [FIELD_NAMES.CURRENCY]: DEFAULT_CURRENCIES[site],
      [FIELD_NAMES.MEASUREMENT_UNIT]: measurementUnitType,
      [FIELD_NAMES.PACKAGE_TYPE]: _.first(
        _.map(
          _.get(eBayDetails, 'ShippingPackage'),
          DEFAULT_FIELD_PROPS[FIELD_NAMES.PACKAGE_TYPE].dataSourceConfig.value
        )
      ),
      [FIELD_NAMES.DURATION]: listingType === LISTING_TYPES.FIXED_PRICE ? GTC : undefined,
      ...defaultTemplateData,
    };
  }
);

export function flattenListingData(originalValues) {
  const flatValues = _.pickBy(_.mapValues(_.mapKeys(FIELD_NAMES), fieldName => _.get(originalValues, fieldName)));
  const itemSpecifics = flatValues[FIELD_NAMES.ITEM_SPECIFICS];
  if (itemSpecifics) {
    _.forEach(itemSpecifics, (value, itemSpecificName) => {
      flatValues[getItemSpecificFieldName(itemSpecificName)] = value;
    });
    flatValues[FIELD_NAMES.ITEM_SPECIFICS] = undefined;
  }
  return flatValues;
}

export function fixItemSpecificsWithDots(unflattenListingData): {} {
  const itemSpecifics = unflattenListingData[FIELD_NAMES.ITEM_SPECIFICS];
  return {
    ...unflattenListingData,
    [FIELD_NAMES.ITEM_SPECIFICS]: itemSpecifics ? flatten(itemSpecifics, {safe: true}) : {},
  };
}

export const getOriginalValues = createSelector(
  [getDefaultValues, getEbayListingWithItemSpecificsRemovedIfNeeded],
  (defaultValues, ebayListing) => {
    return flattenListingData({...defaultValues, ...ebayListing});
  }
);

export const extractBehaviorFromFieldName = (
  fieldName,
  measurementUnitType,
  conditionEnabled,
  conditionValue,
  currency,
  templateId,
  behaviorData
) => {
  switch (fieldName) {
    case FIELD_NAMES.WEIGHT_MAJOR:
    case FIELD_NAMES.WEIGHT_MINOR:
      behaviorData.unit = MEASUREMENT_UNITS[measurementUnitType][fieldName];
      break;
    case FIELD_NAMES.DIMENSION_LENGTH:
    case FIELD_NAMES.DIMENSION_DEPTH:
    case FIELD_NAMES.DIMENSION_WIDTH:
      behaviorData.unit = 'inches';
      break;
    case FIELD_NAMES.START_PRICE:
    case FIELD_NAMES.BUY_IT_NOW_PRICE:
    case FIELD_NAMES.RESERVE_PRICE:
    case FIELD_NAMES.BEST_OFFER_AUTO_ACCEPT_PRICE:
    case FIELD_NAMES.MINIMUM_BEST_OFFER_PRICE:
      behaviorData.unit = currency;
      break;
    case FIELD_NAMES.CONDITION_DESCRIPTION:
      behaviorData.notSupported = !(!conditionEnabled || conditionValue > 1499 || conditionValue < 1000);
      behaviorData.required = false;
      break;
    case FIELD_NAMES.DESCRIPTION:
    case FIELD_NAMES.TITLE:
      behaviorData.disabled = !templateId;
      behaviorData.required = !behaviorData.disabled; // should we apply this to all fields?
      break;
    default:
      break;
  }
  return behaviorData;
};

export const extractBehaviorFromFeature = (feature, categoryFeatures, requiredBehavior, fieldName, behaviorData) => {
  switch (_.get(categoryFeatures, feature)) {
    case REQUIRED:
      behaviorData.required = requiredBehavior === undefined ? true : requiredBehavior;
      break;
    case ENABLED:
      if (fieldName === FIELD_NAMES.EPID) {
        // TODO - this field Enabled means its required!
        behaviorData.required = true;
        break;
      }
      break;
    case DISABLED:
    default:
      if (fieldName === FIELD_NAMES.EPID) {
        // TODO - this field is unpopulated when it optional!
        break;
      }
      behaviorData.notSupported = true;
      break;
  }
  return behaviorData;
};

export const getDefaultPropsForPublishToEbayByFieldName = (
  fieldName,
  categoryFeatures,
  currency,
  measurementUnitType?,
  conditionEnabled?,
  conditionValue?,
  templateId?
) => {
  const {feature, requiredBehavior, required, disabled, ...props} = DEFAULT_FIELD_PROPS[fieldName] as any;
  let behaviorData = {required, disabled, notSupported: undefined, unit: undefined};
  if (feature) {
    const behaviorFromFeature = extractBehaviorFromFeature(
      feature,
      categoryFeatures,
      requiredBehavior,
      fieldName,
      behaviorData
    );
    behaviorData = {...behaviorData, ...behaviorFromFeature};
  }
  const behaviorFromFieldName = extractBehaviorFromFieldName(
    fieldName,
    measurementUnitType,
    conditionEnabled,
    conditionValue,
    currency,
    templateId,
    behaviorData
  );
  behaviorData = {...behaviorData, ...behaviorFromFieldName};
  return {
    ...props,
    ...behaviorData,
    // TODO - disabledTooltip, is this somehow necessary here?
  };
};

export const getDefaultPropsForPublishToEbay = (categoryFeatures, measurementUnitType, currency) => {
  return {
    ..._.mapValues(DEFAULT_FIELD_PROPS, (fieldProps, fieldName) => {
      return getDefaultPropsForPublishToEbayByFieldName(fieldName, categoryFeatures, currency, measurementUnitType);
    }),
  };
};

export const isVariationsListing = createSelector(
  [getEbayListing],
  listingData => {
    return listingData && !!listingData.Variations;
  }
);

const getUpdatedVariationChanges = (state: StoreState) => getUpdatedFieldValueInternal(state, FIELD_NAMES.VARIATIONS);

export const getUpdatedVariations = createSelector(
  [getUpdatedVariationChanges, getEbayListing],
  (updatedVariations, ebayListing) => {
    const listingDataVariations = _.get(ebayListing, FIELD_NAMES.VARIATIONS);
    return getUpdatedVariationsData(listingDataVariations, updatedVariations);
  }
);

export const prepareVariationSpecificsSetForEditDialog = createSelector(
  [getFlatNestedVariationsSpecifics, getVariationSpecificsSet, getUpdatedVariations],
  (flattenNestedVariationsSpecifics, variationsSpecificSet, updatedVariations) => {
    const flattenAspects = {};
    const toDelete = {};
    _.forEach(updatedVariations, variation => {
      const variationSpecifics = variation.VariationSpecifics;
      _.forEach(flattenNestedVariationsSpecifics, (flatten: flattenNestedVariationsType, flattenName) => {
        const {parentName, parentValue, childName} = flatten;
        const nestedAspect = variationSpecifics[parentName];
        if (nestedAspect === parentValue) {
          toDelete[parentName] = true;
          toDelete[childName] = true;
          flattenAspects[flattenName] = _.uniq([...(flattenAspects[flattenName] || []), variationSpecifics[childName]]);
        }
      });
    });
    return {..._.omit(variationsSpecificSet, _.keys(toDelete)), ...flattenAspects};
  }
);

export const getUpdatedVariationsData = (listingDataVariations, updatedVariations) => {
  return _.pickBy({...listingDataVariations, ...updatedVariations}, variation => _.isUndefined(variation.Delete));
};

export const hasVariations = createSelector(
  [getUpdatedVariations],
  variations => {
    return !_.isEmpty(variations);
  }
);

export const getUpdatedVariationsPictureNode = (state: StoreState) =>
  getUpdatedFieldValueInternal(state, FIELD_NAMES.VARIATIONS_PICTURES);

export const getVariationsPictureData = createSelector(
  [getUpdatedVariationsPictureNode],
  variationsPictureData => {
    return _.omit(variationsPictureData, VARIATIONS_SELECTED_ASPECT);
  }
);

export const getSelectedVariation = variationPictures => {
  if (!_.isEmpty(variationPictures)) {
    if (VARIATIONS_SELECTED_ASPECT in variationPictures) {
      return variationPictures[VARIATIONS_SELECTED_ASPECT];
    } else {
      //take first variation as default
      return _.keys(variationPictures)[0];
    }
  }
};

export const getSelectedVariationSelector = createSelector(
  [getUpdatedVariationsPictureNode],
  variationPictures => {
    return getSelectedVariation(variationPictures);
  }
);

export const getSelectedVariationsUrls = (variations, selectedVariation) => {
  if (!_.isEmpty(variations)) {
    return variations[selectedVariation];
  }
  return [];
};

export const getSelectedVariationsPictures = createSelector(
  [getVariationsPictureData, getSelectedVariationSelector],
  (variations, selectedVariation) => {
    return getSelectedVariationsUrls(variations, selectedVariation);
  }
);

// The definition of createSelector is limited to a maximum of 12 selectors
const getDefaultPropsWithoutItemSpecifics = createSelector(
  [
    getCategoryFeatures,
    getShippingType,
    getListingTypeInternal,
    getConditionValue,
    getConditionEnable,
    isNonDraft,
    getTemplateId,
    getMeasurementUnitType,
    getCurrency,
    getProductData,
    hasVariations,
    getSelectedVariationSelector,
  ],
  (
    categoryFeatures,
    shippingType,
    listingType,
    conditionValue,
    conditionEnabled,
    nonDraft,
    templateId,
    measurementUnitType,
    currency,
    productData,
    hasVariations,
    selectedVariation
  ) => {
    return _.mapValues(
      DEFAULT_FIELD_PROPS,
      (
        {
          requiredForAdd,
          required,
          disabled,
          feature,
          requiredBehavior,
          disabledForListingType,
          notSupportedForListingType,
          requiredForShippingType,
          notSupportedForShippingType,
          disabledForEbayCatalogProduct,
          requiredWhenMissingFromProductData,
          floatingLabelText,
          auctionFloatingLabelText,
          disabledByVariations,
          disabledByVariationsTooltip,
          notRequiredIfHasVariationsPhotos,
          type,
          ...props
        }: any,
        fieldName
      ) => {
        let notSupported;
        let unit;
        let behaviorData = {required, disabled, notSupported, unit};
        let disabledTooltip;
        if (nonDraft || fieldName === FIELD_NAMES.DURATION) {
          if (disabledForListingType === listingType) {
            behaviorData.disabled = true;
          }
        } else {
          // draft or new item
          behaviorData.required = required || requiredForAdd;
          behaviorData.disabled = false;
        }
        if (disabledForEbayCatalogProduct) {
          behaviorData.disabled = !_.isEmpty(productData);
          disabledTooltip = DISABLED_BY_PRODUCT;
        }
        if (requiredWhenMissingFromProductData) {
          behaviorData.required = _.isEmpty(_.get(productData, requiredWhenMissingFromProductData));
        }
        if (notRequiredIfHasVariationsPhotos) {
          behaviorData.required = selectedVariation === USE_DEFAULT_PHOTOS;
        }
        if (_.indexOf(requiredForShippingType, shippingType) !== -1) {
          behaviorData.required = true;
        } else if (
          _.indexOf(notSupportedForShippingType, shippingType) !== -1 ||
          notSupportedForListingType === listingType
        ) {
          behaviorData.notSupported = true;
        } else if (feature) {
          const behaviorFromFeature = extractBehaviorFromFeature(
            feature,
            categoryFeatures,
            requiredBehavior,
            fieldName,
            behaviorData
          );
          behaviorData = {...behaviorData, ...behaviorFromFeature};
        }
        const behaviorFromFieldName = extractBehaviorFromFieldName(
          fieldName,
          measurementUnitType,
          conditionEnabled,
          conditionValue,
          currency,
          templateId,
          behaviorData
        );
        behaviorData = {...behaviorData, ...behaviorFromFieldName};
        if (disabledByVariations && hasVariations) {
          behaviorData.disabled = true;
          behaviorData.required = false;
          disabledTooltip = disabledByVariationsTooltip ? disabledByVariationsTooltip : DISABLED_BY_VARIATION;
        }
        return {
          ...props,
          ...behaviorData,
          disabledTooltip,
          type: isNumericType(type) ? 'number' : type,
          floatingLabelText:
            auctionFloatingLabelText && listingType === LISTING_TYPES.AUCTION
              ? auctionFloatingLabelText
              : floatingLabelText,
        };
      }
    );
  }
);

export const getDefaultProps = createSelector(
  [getDefaultPropsWithoutItemSpecifics, getItemSpecificsProps],
  (defaultProps, itemSpecificsProps) => {
    return {...defaultProps, ...itemSpecificsProps};
  }
);

export const getFieldsToRevise = createSelector(
  [getInternalFieldsToRevise, getFieldsToDelete, getDefaultProps, getDefaultValues],
  (internalFieldsToRevise, fieldsToDelete, defaultFieldsProps, defaultValues) => {
    return _.omitBy({...defaultValues, ...internalFieldsToRevise}, (value, fieldName) => {
      return fieldsToDelete[fieldName] || (defaultFieldsProps[fieldName] && defaultFieldsProps[fieldName].notSupported);
    });
  }
);

export const getUpdatedEbayListingValuesForSave = createSelector(
  [getEbayListingWithItemSpecificsRemovedIfNeeded, getFieldsToRevise, getFieldsToDelete, isBusinessPolicyOptedIn],
  (ebayListing, fieldsToRevise, fieldsToDelete, isBusinessPolicyOptedIn) => {
    let clone = ebayListing ? _.cloneDeep(ebayListing) : {};
    const newCategory = _.get(fieldsToRevise, FIELD_NAMES.PRIMARY_CATEGORY);
    if (newCategory && newCategory !== _.get(ebayListing, FIELD_NAMES.PRIMARY_CATEGORY)) {
      clone = _.omit(clone, 'ItemSpecifics');
    }
    _.forEach(fieldsToRevise, (value, key) => {
      if (key === FIELD_NAMES.VARIATIONS && _.get(clone, key)) {
        _.set(clone, key, {..._.get(clone, key), ...value});
      } else {
        _.set(clone, key, value);
        clone = fixItemSpecificsWithDots(clone);
      }
    });
    _.forEach(fieldsToDelete, (value, key) => {
      _.unset(clone, key);
    });
    let state = getCurrentState();
    _.set(clone, FIELD_NAMES.MEASUREMENT_UNIT, getMeasurementUnitType(state));
    clone[FIELD_NAMES.BUSINESS_POLICY_OPTED_IN] = isBusinessPolicyOptedIn;
    return clone;
  }
);

export const getUpdatedEbayListingValues = createSelector(
  [getUpdatedEbayListingValuesForSave, getProductData],
  (clone, productData) => {
    if (!_.isEmpty(productData)) {
      clone = _.cloneDeep(clone);
      _.forEach(productData.aspects, ({localizedName, localizedValues}) => {
        _.set(clone, getItemSpecificFieldName(localizedName), localizedValues);
      });
      _.set(clone, FIELD_NAMES.PRIMARY_CATEGORY, productData.primaryCategoryId);
      _.set(clone, FIELD_NAMES.UPC, productData.upc);
      _.set(clone, FIELD_NAMES.ISBN, productData.isbn);
      _.set(clone, FIELD_NAMES.EAN, productData.ean);
      if (productData.brand) {
        _.set(clone, getItemSpecificFieldName('Brand'), productData.brand);
      }
    }
    return clone;
  }
);

export const getEbayPhotoIdFromUrl = url => {
  if (!isEbayImage(url)) {
    return url;
  }
  const splitUrl = _.split(url, '/');
  if (splitUrl.length === 7) {
    return splitUrl[5];
  }
  return splitUrl[7];
};

export function getPhotosInOtherNotInEbay(ebayUrlsSet, otherUrlSet) {
  _.forEach(ebayUrlsSet, ebayUrl => {
    const id = getEbayPhotoIdFromUrl(ebayUrl);
    _.forEach(otherUrlSet, otherUrl => {
      if (_.includes(otherUrl, id)) {
        otherUrlSet = _.without(otherUrlSet, otherUrl);
      }
    });
  });
  return otherUrlSet;
}

export function getPhotosInEbayNotInOther(ebayUrlSet, otherUrlSet) {
  const diff = [];
  _.forEach(ebayUrlSet, ebayUrl => {
    const id = getEbayPhotoIdFromUrl(ebayUrl);
    let found = false;
    _.forEach(otherUrlSet, otherUrl => {
      if (_.includes(otherUrl, id)) {
        found = true;
      }
    });
    if (!found) {
      diff.push(ebayUrl);
    }
  });
  return diff;
}

export const getPhotosUnion = (ebayUrlSet, otherUrlSet) => {
  const unionUrls = ebayUrlSet;
  _.forEach(ebayUrlSet, ebayUrl => {
    const id = getEbayPhotoIdFromUrl(ebayUrl);
    _.forEach(otherUrlSet, otherUrl => {
      if (_.includes(otherUrl, id)) {
        otherUrlSet = _.without(otherUrlSet, otherUrl);
      }
    });
  });
  return _.union(otherUrlSet, unionUrls);
};

export const getUpdatedFieldValue = (state, fieldName: string) => {
  if (fieldName === FIELD_NAMES.VARIATIONS) return getUpdatedVariations(state);
  const updatedEbayListingValues = getUpdatedEbayListingValues(state);
  if (updatedEbayListingValues[FIELD_NAMES.ITEM_SPECIFICS] && isItemSpecificsFieldName(fieldName)) {
    return updatedEbayListingValues[FIELD_NAMES.ITEM_SPECIFICS][getItemSpecificWithoutPrefix(fieldName)];
  }
  return _.get(updatedEbayListingValues, fieldName);
};

export const isDirty = (state: StoreState) =>
  (state.routing.location.pathname === MyAccount.EbayListing && !_.isEmpty(getFieldsToReviseDiffFromDefault(state))) ||
  !_.isEmpty(state.ebayListing.fieldsToDelete);
export const isDirtyField = (state, fieldName) =>
  getFieldsToRevise(state)[fieldName] !== undefined || state.ebayListing.fieldsToDelete[fieldName] !== undefined;

const isPhotosPickerGroupEmpty = value => {
  const selectedAspect = value && value[VARIATIONS_SELECTED_ASPECT];
  return !value || (selectedAspect !== USE_DEFAULT_PHOTOS && _.isEmpty(_.flatMap(value[selectedAspect])));
};

export function isFieldEmpty(value, {displayType = undefined, type = undefined} = {}) {
  switch (displayType) {
    case DISPLAY_TYPES.CUSTOM_ERROR:
      return !value;
    case DISPLAY_TYPES.TOGGLE:
      return false;
    case DISPLAY_TYPES.PHOTOS_PICKER_GROUP:
      return !_.isUndefined(value) && isPhotosPickerGroupEmpty(value);
    case DISPLAY_TYPES.TEMPLATE_SELECTOR:
    case DISPLAY_TYPES.FREE_TEXT:
    case DISPLAY_TYPES.SINGLE_SELECT:
    case DISPLAY_TYPES.CATEGORY_SELECTOR:
      if (type === 'number') {
        if (_.isObject(value)) {
          value = value.value;
        }
        return value === 0 || _.isNil(value);
      }
    // eslint-disable-next-line no-fallthrough
    default:
      return _.isEmpty(value);
  }
}

export function isFieldEqual(a, b, props = {}) {
  if (a === b) {
    return true;
  }
  if (_.isObject(a) && _.isObject(b)) {
    return shallowEqual(a, b);
  }
  return isFieldEmpty(a, props) && isFieldEmpty(b, props);
}

export const getFieldsToReviseDiffFromDefault = createSelector(
  [getDefaultValues, getFieldsToRevise],
  (defaultValues, fieldsToRevise) => {
    const filteredFieldsToRevise = _.filter(fieldsToRevise, value => !_.isNil(value));
    return _.difference(_.values(filteredFieldsToRevise), _.values(defaultValues));
  }
);

export const getRequiredFields = createSelector(
  [getDefaultProps],
  defaultProps => {
    return _.pickBy(_.mapValues(defaultProps, ({required}) => required));
  }
);

export const getNotSupportedFields = createSelector(
  [getDefaultProps],
  defaultProps => {
    return _.pickBy(_.mapValues(defaultProps, ({notSupported}) => notSupported));
  }
);

export const getErrorTexts = createSelector(
  [getUpdatedEbayListingValues, getDefaultProps, getProductData, getVariationsEnable, hasVariations],
  (fieldsValues, defaultProps, productData, variationsEnabled, hasVariations) => {
    const errorTexts = {};
    _.forEach(defaultProps, ({maxLength, required, floatingLabelText, errorListener, disabled}, fieldName) => {
      const value = _.get(fieldsValues, fieldName);
      if (!disabled && required && isFieldEmpty(value, defaultProps[fieldName])) {
        errorTexts[fieldName] = defaultProps[fieldName].requiredErrorMessage || 'This field is required';
      } else if (maxLength && _.size(value) > maxLength) {
        errorTexts[fieldName] = `${floatingLabelText} can't be more than ${maxLength} characters`;
      } else if (errorListener && productData && productData.error) {
        errorTexts[fieldName] = productData.error;
      } else if (fieldName === FIELD_NAMES.PRIMARY_CATEGORY && !variationsEnabled && hasVariations) {
        errorTexts[fieldName] = "This category doesn't support variations";
      }
    });
    return errorTexts;
  }
);

export const getAllVariationsDeleted = listingData => {
  return _.reduce(
    _.get(listingData, FIELD_NAMES.VARIATIONS),
    (deletedVariations, v, id) => ({...deletedVariations, ...{[id]: deleteVariation(v)}}),
    {}
  );
};

export const getAllEbayListingVariationsDeleted = createSelector(
  [getEbayListing],
  listingData => {
    return getAllVariationsDeleted(listingData);
  }
);

export const getVariationsSum = createSelector(
  [getSelectedVariationsPictures],
  variationsPictures => {
    let sum = 0;
    _.forEach(variationsPictures, urls => (sum += urls.length));
    return sum;
  }
);

function getVariationsURLsSorted(variationsUrls) {
  const sortedUrls = {}; // 1st image of each variant then 2nd image of each variant etc.
  _.forEach(variationsUrls, variation => {
    let bucket = 0;
    _.forEach(variation, pictureURL => {
      if (_.isUndefined(sortedUrls[bucket])) {
        sortedUrls[bucket] = [];
      }
      sortedUrls[bucket].push(pictureURL);
      ++bucket;
    });
  });
  return sortedUrls;
}

export function getEbayAndVariationUrlsList(ebayGalleryUrls, variationsUrls) {
  const sortedVariationsURLs = _.flatMap(getVariationsURLsSorted(variationsUrls));
  return _.isUndefined(ebayGalleryUrls) ? sortedVariationsURLs : _.concat(ebayGalleryUrls, sortedVariationsURLs);
}

export const getEbayAndVariationUrls = createSelector(
  [getEbayGalleryUrls, getSelectedVariationsPictures],
  (ebayGalleryUrls, variationsPictures) => {
    return getEbayAndVariationUrlsList(ebayGalleryUrls, variationsPictures);
  }
);

export const getVariationProductListingDetailsType = createSelector(
  [getCategoryFeatures],
  categoryFeatures => {
    if (_.isEmpty(categoryFeatures)) {
      return [];
    }
    const permittedTypes = [];
    _.forEach(PRODUCT_IDENTIFIER_FIELDS, fieldName => {
      if (_.includes([REQUIRED, ENABLED], _.get(categoryFeatures, DEFAULT_FIELD_PROPS[fieldName].feature))) {
        permittedTypes.push(DEFAULT_FIELD_PROPS[fieldName].floatingLabelText);
      }
    });
    return permittedTypes;
  }
);

export const getVariationThumbnails = createSelector(
  [getUpdatedVariations, getVariationsPictureData, getSelectedVariationSelector, getEbayGalleryUrls],
  (variations, variationURLs, selectedVariation, ebayGalleryUrls) => {
    const variationThumbnails = {};
    _.forEach(variations, (variation, key) => {
      let firstURL = '';
      _.forEach(variation.VariationSpecifics, (aspectValue, aspectName) => {
        if (aspectName === selectedVariation) {
          firstURL = variationURLs[selectedVariation][aspectValue][0];
        }
      });
      if (_.isEmpty(firstURL) && !_.isEmpty(ebayGalleryUrls)) {
        firstURL = ebayGalleryUrls[0];
      }
      variationThumbnails[key] = firstURL;
    });
    return variationThumbnails;
  }
);

export const getVariationsTableRows = createSelector(
  [getUpdatedVariations, getVariationThumbnails],
  (variations, variationThumbnails) => {
    const totalVariationsData = [];
    let index = 0;
    _.forEach(variations, (variation, variationKey) => {
      const variationData = {
        _id: variationKey,
        thumbnail: variationThumbnails[variationKey],
        ...variation.VariationProductListingDetails,
        ..._.pick(variation, [FIELD_NAMES.SKU, FIELD_NAMES.START_PRICE, FIELD_NAMES.QUANTITY]),
        ...variation.VariationSpecifics,
      };
      totalVariationsData.push([index++, variationData]);
    });
    return Map(totalVariationsData);
  }
);

export const getVariationsColumns = createSelector(
  [getVariationProductListingDetailsType, getVariationAspectNames, getNestedVariationSpecifics],
  (variationProductListingDetailsType, variationNames, nestedVariationSpecifics) => {
    const columns: ColumnsType = {};
    columns[FIELD_NAMES.SKU] = {
      type: 'text',
    };
    columns[FIELD_NAMES.START_PRICE] = {
      displayName: 'Price',
      type: DEFAULT_FIELD_PROPS[FIELD_NAMES.START_PRICE].type as ColumnType,
      validator: compose(
        nonEmpty,
        positive
      ),
    };
    columns[FIELD_NAMES.QUANTITY] = {
      type: DEFAULT_FIELD_PROPS[FIELD_NAMES.QUANTITY].type as ColumnType,
      validator: nonEmpty,
    };
    _.forEach(variationProductListingDetailsType, columnName => {
      columns[columnName] = {type: 'text'};
    });
    _.forEach(variationNames, columnName => {
      columns[columnName] = {type: 'text', readOnly: true};
      if (nestedVariationSpecifics[columnName]) {
        columns[columnName].children = _.keys(nestedVariationSpecifics[columnName].children);
      }
    });
    return columns;
  }
);

export const getPhotosShowMode = createSelector(
  [getEbayAndVariationUrls, getDescriptionGalleryUrls],
  (ebayWithVariationsUrls, descriptionGalleryUrls) => {
    const inEbayWithVariationsButNotDescription = getPhotosInEbayNotInOther(
      ebayWithVariationsUrls,
      descriptionGalleryUrls
    );
    if (_.isEmpty(inEbayWithVariationsButNotDescription)) {
      return SHOW_MODES.SHOW_ALL;
    } else if (_.isEqual(inEbayWithVariationsButNotDescription, ebayWithVariationsUrls)) {
      return SHOW_MODES.SHOW_NONE;
    }
    return SHOW_MODES.SHOW_SOME;
  }
);

const getSite = state => getUpdatedFieldValue(state, FIELD_NAMES.EBAY_SITE);

const sectionDependencies = {
  [ADD_OR_EDIT.ADD]: {
    [PRODUCT_SECTION]: ['storeCategories'],
    [VARIATIONS_SECTION]: ['categoryFeatures'],
    [SELLING_SECTION]: ['categoryFeatures'],
    [PHOTOS_SECTION]: ['site'],
    [ITEM_DESCRIPTION_SECTION]: [],
    [POLICIES_SECTION]: ['sellerPolicies', 'eBayDetails'],
  },
  [ADD_OR_EDIT.EDIT]: {
    [PRODUCT_SECTION]: ['listingData', 'storeCategories'],
    [VARIATIONS_SECTION]: ['listingData', 'categoryFeatures'],
    [SELLING_SECTION]: ['listingData', 'categoryFeatures'],
    [PHOTOS_SECTION]: ['listingData', 'site'],
    [ITEM_DESCRIPTION_SECTION]: ['listingData'],
    [POLICIES_SECTION]: ['listingData', 'sellerPolicies', 'eBayDetails'],
  },
};
export const getReadySections = createSelector(
  [
    getSite,
    getEbayListing,
    getSellerPolicies,
    getSellerStoreCategories,
    addOrEdit,
    getEbayDetails,
    getCategoryFeatures,
  ],
  (site, listingData, sellerPolicies, storeCategories, addOrEdit, eBayDetails, categoryFeatures) => {
    const readySections = {};
    const data = {site, listingData, sellerPolicies, storeCategories, eBayDetails, categoryFeatures};
    _.forEach(sectionDependencies[addOrEdit], (dependencies, section) => {
      readySections[section] = !_.some(dependencies, dependency => data[dependency] === undefined);
    });
    return readySections;
  }
);

export const getDurationDataSet = (features, listingType = LISTING_TYPES.FIXED_PRICE) => {
  return _.get(features, `ListingDuration.${listingType}`);
};

export const getVariationFieldNames = createSelector(
  [getVariationProductListingDetailsType, getVariationSpecificNames],
  (variationProductListingDetailsType, variationSpecificNames) => {
    return [
      FIELD_NAMES.START_PRICE,
      FIELD_NAMES.QUANTITY,
      FIELD_NAMES.SKU,
      ..._.map(variationProductListingDetailsType, value => PRODUCT_LISTING_DETAILS_PREFIX + value),
      ...variationSpecificNames,
    ];
  }
);

export const getListingTitle = createSelector(
  [getEbayListing],
  listingData => {
    return listingData && listingData.Title;
  }
);
